import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {repoAppBuilder} from '@github-ui/repo-app-builder'
import type {ShouldRevalidateFunction} from '@github-ui/react-router'
import type {Workflow} from '../views/models'
import type {WorkflowRouteParams} from '../workflow-path'

export interface ActionsSurveyPayload {
  surveyLink: string
  surveyOpenCallbackPath: string
  surveyDismissCallbackPath: string
}

/** Payload for the stateless GitHub App installation-token format notice. */
export interface StatelessS2sTokenFormatChangeBannerPayload {
  /** Path that stores the current user's 90-day notice dismissal. */
  dismissPath: string
}

/** Payload for the repository Actions rate-limit warning banner. */
export interface RepositoryRateLimitBannerPayload {
  /** Customer-facing banner title, rendered as-is. */
  title: string
  /** Customer-facing banner message, rendered as-is. */
  message: string
  /** URL for an inline "Learn more" link. */
  documentationUrl: string
}

/** Payload for the Actions runner-capacity warning banner. */
export interface RunnerCapacityBannerPayload {
  /** Customer-facing banner message, rendered as-is. */
  message: string
}

/**
 * Payload for the Actions billing-warning banner
 */
export interface BillingWarningBannerPayload {
  /** Which body copy branch to render; `other` renders the heading with no body (existing Rails gap). */
  variant: 'legacy_plan' | 'access_not_included' | 'disabled' | 'other'
  /** The billing owner's display login, bolded in the `access_not_included` copy. */
  ownerLogin: string
  /** Path to the org billing/plans settings page for the "Upgrade" button; present only when the viewer may see it. */
  upgradePath?: string | null
  /** URL for the "Learn more about GitHub Actions" link; present only when the org-upsell row should render. */
  learnMoreUrl?: string | null
}

/**
 * Payload for the billing-critical (trust-tier usage limit) banner
 */
export interface BillingCriticalBannerPayload {
  /** Whether the viewer is an admin of the billable owner; selects the admin vs. non-admin copy. */
  isAdmin: boolean
}

export interface WorkflowsLayoutPayload {
  workflows: Workflow[]
  totalCount: number
  pinnedWorkflowIds: number[]
  allowPinning: boolean
  canCreateWorkflow: boolean
  showOnlyRequiredWorkflows?: boolean
  showDeployments: boolean
  showAttestations: boolean
  showRunners: boolean
  lockfilePath?: string | null
  showUsageMetrics: boolean
  isFork: boolean
  workflowsPath?: string | null
  showActionsNotEnabledBanner: boolean
  banners: {
    enablePath: string
    actionsSurvey?: ActionsSurveyPayload
    statelessS2sTokenFormatChangeBanner?: StatelessS2sTokenFormatChangeBannerPayload
    /** Absent or `null` when the Launch RPC lookup fails or no rate limits are active; present on success with an active rate limit. */
    repositoryRateLimitBanner?: RepositoryRateLimitBannerPayload | null
    /** Present when GitHub-hosted runner capacity is constrained for the repository. */
    runnerCapacityBanner?: RunnerCapacityBannerPayload | null
    /** Present when the repository owner's `Billing::ActionsPermission` check fails (legacy plan, billing lock, or another not-allowed reason). */
    billingWarningBanner?: BillingWarningBannerPayload | null
    /** Present when the repository's billable owner has hit its trust-tier usage limit. */
    billingCriticalBanner?: BillingCriticalBannerPayload | null
  }
  // Server-read seed of the shared sidebar cookie, used to keep SSR and hydration in sync.
  sidebarExpanded?: boolean | null
}

/**
 * Revalidates the repository-scoped workflows layout when its repository, relevant
 * search params, or containing Actions surface changes.
 */
export const shouldRevalidateWorkflowsLayout: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
  currentUrl,
  nextUrl,
}) => {
  return (
    currentParams.owner !== nextParams.owner ||
    currentParams.repo !== nextParams.repo ||
    currentUrl.searchParams.get('lab') !== nextUrl.searchParams.get('lab') ||
    isWorkflowSurface(currentUrl.pathname) !== isWorkflowSurface(nextUrl.pathname)
  )
}

function isWorkflowSurface(pathname: string) {
  const normalizedPathname = pathname.replace(/\/+$/, '')
  return !normalizedPathname.endsWith('/actions/caches') && !normalizedPathname.endsWith('/actions/runners')
}

/**
 * Builds the query dependencies for the repository-scoped workflows-layout fetch.
 */
export function workflowsLayoutQueryDeps({
  pathname,
  params,
  searchParams,
}: {
  pathname: string
  params: WorkflowRouteParams
  searchParams: URLSearchParams
}) {
  return {
    pathname: `/${encodeURIComponent(params.owner ?? '')}/${encodeURIComponent(params.repo ?? '')}/actions/_layout`,
    searchParams: {
      lab: searchParams.get('lab') === 'true' ? 'true' : null,
      workflow_runs_view: isWorkflowSurface(pathname) ? 'true' : null,
    },
  }
}

export function buildWorkflowsLayoutRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('workflowsLayoutRoute', {
    path: '/:owner/:repo/actions',
    queries: [
      mainQuery<WorkflowsLayoutPayload>({
        queryDeps: workflowsLayoutQueryDeps,
        staleTimeForNavigation: 360000,
      }),
    ],
    shouldRevalidate: shouldRevalidateWorkflowsLayout,
  })
}

export const workflowsLayoutRoute = buildWorkflowsLayoutRoute(repoAppBuilder)
